/**
 * Pending admissions (1.3): what has been accepted and reserved and has not
 * started, sorted by expected discharge. Each row carries the patient, the
 * expected plan, the expected discharge, the current hold, and three actions:
 * Release, Update, Welcome call.
 *
 * A row **leaves** when the welcome call converts its hold into a real start of
 * care (the patient page carries the patient from there) or when it is released.
 * There is no converted group: the proof the call worked is the visit in the
 * schedule, not a line on this list.
 *
 * Readiness stays internal (it decides FFS-on-welcome-call and MA-on-auth); it is
 * never a strip on the screen. A hold whose day passed without converting reads
 * as expired and is released by hand — never silently.
 */
import type { DateKey, DemoState, Discipline, IsoString, Payer, Referral } from "@caredna-marlin/utils/homeHealthDemo/state";
import { selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { expectedPlanFor } from "./expectedPlan";
import { isHoldExpired, socDueByIso } from "./holds";

export type PendingRowState = "live" | "expired";

export interface PendingTailLine {
  discipline: Discipline;
  requiredTraits: string[];
  pointsPerWeek: number;
  weeks: number;
}

export interface PendingRow {
  holdId: string;
  patientRef: string;
  patientName: string;
  payer: Payer;
  source: string;
  /** Disciplines from the expected plan, in order. */
  disciplines: Discipline[];
  /** What the expected plan reads as: a template name, "Orders on file" or "Custom". */
  expectedPlanLabel: string;
  requiredTraits: string[];
  expectedDischarge: DateKey;
  socDueByIso: IsoString;
  state: PendingRowState;
  /** Set when intake reported a new discharge date and the hold has not moved yet. */
  dischargeChanged: boolean;
  clinicianHandle?: string;
  clinicianName?: string;
  date: DateKey;
  windowStart: string;
  windowEnd: string;
  tail: PendingTailLine[];
  /** True while the welcome call is still outstanding — the row's primary action. */
  welcomeCallOpen: boolean;
  /**
   * The welcome call is done and the hold is waiting on the payer, not on
   * anyone in the branch. Medicare FFS converts on the call; Medicare Advantage
   * converts when the SOC auth lands, so an MA row sits here in between with
   * three actions that have nothing left to do for it.
   */
  awaitingAuth: boolean;
  /** The hold's day is on or before today: the SOC clock is out of room. */
  socByToday: boolean;
}

/** A hold that passed its day without converting. */
export function pendingRowState(state: DemoState, holdId: string, today: DateKey): PendingRowState {
  const hold = state.holds[holdId];
  return hold && isHoldExpired(hold, today) ? "expired" : "live";
}

export function selectPendingList(state: DemoState, now: Date = new Date()): PendingRow[] {
  const tz = state.config.branch.timeZone;
  const today = toDateKey(selectDemoNow(state, now), tz);
  const rows: PendingRow[] = [];
  for (const hold of Object.values(state.holds)) {
    // Converted and released holds are off the list: accepted-and-not-started only.
    if (hold.status !== "held") continue;
    const referral: Referral | undefined = state.referrals[hold.referralId];
    const patient = state.patients[hold.patientRef];
    if (!referral || !patient) continue;
    const plan = expectedPlanFor(state.config, referral);
    const orders = plan.orders;
    const readiness = state.readiness[hold.id] ?? {};
    const clinician = hold.providerHandle ? state.roster[hold.providerHandle] : undefined;
    rows.push({
      holdId: hold.id,
      patientRef: hold.patientRef,
      patientName: patient.displayName,
      payer: patient.payer,
      source: referral.source,
      disciplines: Array.from(new Set(orders.map((o) => o.discipline))),
      expectedPlanLabel: plan.label,
      requiredTraits: Array.from(new Set(orders.flatMap((o) => o.requiredTraits ?? []))),
      expectedDischarge: referral.expectedDischarge,
      socDueByIso: socDueByIso(referral, tz),
      state: pendingRowState(state, hold.id, today),
      dischargeChanged: !!referral.dischargeChangedAtIso,
      clinicianHandle: hold.providerHandle,
      clinicianName: clinician?.displayName,
      date: hold.date,
      windowStart: hold.windowStart,
      windowEnd: hold.windowEnd,
      tail: hold.tail.map((t) => ({ discipline: t.discipline, requiredTraits: t.requiredTraits, pointsPerWeek: t.pointsPerWeek, weeks: t.weeks })),
      welcomeCallOpen: hold.status === "held" && readiness.readinessCall !== undefined && readiness.readinessCall !== "done",
      awaitingAuth: patient.payer === "MA" && readiness.readinessCall === "done" && readiness.auth !== undefined && readiness.auth !== "done",
      socByToday: toDateKey(new Date(socDueByIso(referral, tz)), tz) <= today,
    });
  }
  return rows.sort((a, b) => a.expectedDischarge.localeCompare(b.expectedDischarge) || a.socDueByIso.localeCompare(b.socDueByIso) || a.patientName.localeCompare(b.patientName));
}
